import React from 'react'
import {
  ButtonVariant,
  Highlight,
  MaxWidthConstraint,
  OneByFourGrid,
  OvalButton,
  Spacer,
  WorkArea,
} from '@revelrygroup/components'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { API } from '../revelry_api'
import { FuneralHome, ObituarySnippet } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import H3 from '../components/typography/H3'
import Layout from '../components/Layout'
import Obituaries from '../components/Obituaries'
import FakeRightArrowLink from '../components/buttons/FakeRightArrowLink'
import { routes } from '../routes'
import SocialFeature from '../components/SocialFeature'
import styles from '../styles/Home.module.scss'
import { Testimonials } from '../components/Testimonials'
import VideoHero from '../components/VideoHero'

interface Props {
  funeralHome: FuneralHome | null
  obituaries: ObituarySnippet[]
}

const Home: React.FC<Props> = ({ funeralHome, obituaries }) => {
  return (
    <>
      <Head>
        <title>
          {funeralHome?.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          key="description"
          content={`${funeralHome?.name} serves the ${FuneralHomeModel.headTagLocation(
            funeralHome,
            ' and ',
          )}${' '}
          ${
            funeralHome?.location_2_city ? 'regions' : 'region'
          } with obituaries and funeral services such as memorials, burials, viewings, embalmings, death planning and other end of life services. Contact us for consultation.`}
        />
      </Head>

      <Layout headerVariant="brandedLightV2">
        <VideoHero />

        <div className="height-100">
          <main>
            <WorkArea className={styles.highlightWorkarea}>
              <MaxWidthConstraint maxWidth={950}>
                <Spacer verticalBottom="sm" verticalLgBottom="md">
                  <h2 className={styles.heroHeadline}>
                    Nearly 140 Years of Service. Five Generations of Family Ownership.
                    <br />
                    One Enduring Commitment to the Families We Serve.
                  </h2>

                  <Spacer verticalTop="sm">
                    <p className="body centered-text">
                      By taking the time to understand your loved one&apos;s life, values, and
                      legacy, we&apos;ll help you create a service that reflects the unique life
                      they lived. Our experienced team is here to guide your family with compassion,
                      dignity, and personalized care, whether you&apos;re in Maryland, Washington,
                      D.C., Northern Virginia, or coordinating arrangements from across the country.
                    </p>
                  </Spacer>
                </Spacer>
              </MaxWidthConstraint>

              <MaxWidthConstraint>
                <OneByFourGrid className={styles.featureGrid}>
                  <Link href={routes.about}>
                    <a>
                      <Highlight.Wrapper>
                        <Highlight.Primary>
                          <Image
                            alt="Kalas team"
                            height={380}
                            layout="responsive"
                            src="/photo-hp-about-us@2x.jpg"
                            width={380}
                          />
                        </Highlight.Primary>

                        <Highlight.Secondary className={styles.highlightSecondary}>
                          <H3>About Us</H3>

                          <FakeRightArrowLink className={styles.highlightButton} />
                        </Highlight.Secondary>
                      </Highlight.Wrapper>
                    </a>
                  </Link>

                  <Link href={routes.howWeCelebrate}>
                    <a>
                      <Highlight.Wrapper>
                        <Highlight.Primary>
                          <Image
                            alt="Kalas Funeral Home & Crematory"
                            height={380}
                            layout="responsive"
                            src="/photo-hp-kalas-difference@2x.jpg"
                            width={380}
                          />
                        </Highlight.Primary>

                        <Highlight.Secondary className={styles.highlightSecondary}>
                          <H3>The Kalas Difference</H3>

                          <FakeRightArrowLink className={styles.highlightButton} />
                        </Highlight.Secondary>
                      </Highlight.Wrapper>
                    </a>
                  </Link>

                  <Link href={routes.veteranServices}>
                    <a>
                      <Highlight.Wrapper>
                        <Highlight.Primary>
                          <Image
                            alt="Folded American flag"
                            height={380}
                            layout="responsive"
                            src="/photo-hp-veteran-services@2x.jpg"
                            width={380}
                          />
                        </Highlight.Primary>

                        <Highlight.Secondary className={styles.highlightSecondary}>
                          <H3>Veteran Services</H3>

                          <FakeRightArrowLink className={styles.highlightButton} />
                        </Highlight.Secondary>
                      </Highlight.Wrapper>
                    </a>
                  </Link>

                  <Link href={routes.planningAhead}>
                    <a>
                      <Highlight.Wrapper>
                        <Highlight.Primary>
                          <Image
                            alt="Planning ahead"
                            height={380}
                            layout="responsive"
                            src="/photo-hp-planning-ahead@2x.jpg"
                            width={380}
                          />
                        </Highlight.Primary>

                        <Highlight.Secondary className={styles.highlightSecondary}>
                          <H3>Planning Ahead</H3>

                          <FakeRightArrowLink className={styles.highlightButton} />
                        </Highlight.Secondary>
                      </Highlight.Wrapper>
                    </a>
                  </Link>
                </OneByFourGrid>
              </MaxWidthConstraint>
            </WorkArea>

            <WorkArea className={styles.obituaryWorkArea}>
              <h2 className={styles.h2}>Celebrating the Life of...</h2>

              <MaxWidthConstraint>
                <Obituaries obituaries={obituaries} />
              </MaxWidthConstraint>

              <div className={styles.allCelebrationsBtnWrapper}>
                <OvalButton
                  as="Link"
                  href={routes.obituaries}
                  variant={ButtonVariant.INVERSE_LIGHT}
                >
                  More Obituaries
                </OvalButton>
              </div>
            </WorkArea>

            <SocialFeature />

            <Testimonials />
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const [obituaries, funeralHome] = await Promise.all([
      API.obituarySnippets.loadAll({ limit: 12, page: 1 }),
      API.funeralHomes.load(),
    ])

    return { props: { obituaries, funeralHome } }
  } catch (e) {
    return { props: { obituaries: [], funeralHome: null } }
  }
}

export default Home
